import { XIcon, BookMarkedIcon, DatabaseIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router';

import {
  SidebarWrapper,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useAccount } from '@/hooks/useAccount';
import useCollections from '@/hooks/useCollections';
import { DISMISSABLE_ALERT_IDS, useDismissableAlert } from '@/hooks/useDismissableAlert';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { setSidebarContent } from '@/redux/sidebar/actions';
import { COLLECTIONS_DOCS_URL } from '@/util/constants';
import { clampPercentage, isUnlimited } from '@/util/limits';

import { UpgradePlanDescription } from './UpgradePlanDescription';

export const CollectionDetails = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { account } = useAccount();
  const { totalFragments, totalCount, maxCollections } = useCollections();
  const fragmentsPercentage = clampPercentage(totalFragments, account?.max_fragments);

  const { visible, dismiss } = useDismissableAlert(DISMISSABLE_ALERT_IDS.COLLECTIONS_DETAILS);

  const handleClose = () => {
    dispatch(setSidebarContent(null));
  };

  // Close the sidebar when the Escape key is pressed
  useEscapeKey(handleClose);

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle>
        <h2 className="text-base leading-5 font-medium">{t('try_it.headers.details')}</h2>
        <Button variant="ghost-neutral" size="icon-sm" type="button" onClick={handleClose}>
          <XIcon size={16} className="text-muted-foreground" />
        </Button>
      </SidebarWrapperTitle>

      <div className="p-6">
        {visible && (
          <Alert variant="info" className="relative mb-8">
            <Button
              variant="ghost-neutral"
              size="icon-xss"
              type="button"
              onClick={dismiss}
              className="absolute top-3 right-3"
              aria-label={t('common.close')}
            >
              <XIcon size={16} />
            </Button>

            <AlertTitle>{t('collections.details.alert.title')}</AlertTitle>
            <AlertDescription className="max-w-md">
              {t('collections.details.alert.description')}
              <Link
                to={COLLECTIONS_DOCS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-primary"
              >
                {t('common.learn_more')}
              </Link>
            </AlertDescription>
          </Alert>
        )}

        <div className="mb-3 flex items-center gap-1">
          <BookMarkedIcon size={16} className="text-muted-foreground" />
          <h3 className="text-sm leading-6 font-semibold">
            {t('collections.details.collections_usage.title')}
          </h3>
        </div>

        <div className="max-w-lg space-y-1">
          <div className="flex items-center justify-between gap-3">
            {!isUnlimited(maxCollections) && (
              <Progress
                className="max-w-[75%]"
                value={clampPercentage(totalCount, maxCollections)}
              />
            )}
            <p className="text-xs leading-5 font-medium whitespace-nowrap">
              {isUnlimited(maxCollections)
                ? t('table.resource.collection', { count: totalCount ?? 0 })
                : t('collections.details.out_of', { 0: totalCount ?? 0, 1: maxCollections })}
            </p>
          </div>

          {!isUnlimited(maxCollections) && (
            <UpgradePlanDescription i18nKey="collections.details.collections_usage.description" />
          )}
        </div>
      </div>

      <div className="p-6">
        <div className="max-w-lg space-y-1">
          <div className="mb-3 flex items-center gap-1">
            <DatabaseIcon size={16} className="text-muted-foreground" />
            <h3 className="text-sm leading-6 font-semibold">
              {t('collections.details.fragment_usage.title')}
            </h3>
          </div>

          <div className="space-y-1">
            <div className="flex items-center justify-between gap-3">
              {!isUnlimited(account?.max_fragments) && (
                <Progress className="max-w-[75%]" value={fragmentsPercentage} />
              )}
              <p className="text-xs leading-5 font-medium whitespace-nowrap">
                {isUnlimited(account?.max_fragments)
                  ? t('collections.details.count', { count: totalFragments ?? 0 })
                  : `${fragmentsPercentage}%`}
              </p>
            </div>

            {!isUnlimited(account?.max_fragments) && (
              <UpgradePlanDescription
                i18nKey="collections.details.fragment_usage.description"
                values={{ 0: account?.max_fragments }}
              />
            )}
          </div>
        </div>
      </div>
    </SidebarWrapper>
  );
};
